import { VFC, ReactElement, ComponentPropsWithoutRef } from 'react';
import trackUserEvent from '@/util/eventTracking';
import Link from 'next/link';
import cn from 'classnames';

import { TrendArrow } from 'ui';
import { Structure } from '@/types/app';

import pFormatter from '@/util/helpers/pFormatter';
import AssetLogo from '@/components/global/AssetLogo';

/**
 * A card that shows price information on a particular instrument.
 * @component
 */
const Card: VFC<
  {
    instrument?: Structure.AssetInfo;
    category: string;
  } & ComponentPropsWithoutRef<'div'>
> = ({ instrument, className, category, ...props }): ReactElement => {
  if (!instrument)
    return (
      <div
        className={cn('flex flex-col p-3 rounded-xl border border-gray-6 animate-pulse', className)}
        {...props}
      >
        {/* category */}
        <div className="h-[22px] w-1/2 rounded-[4px] bg-gray-6" />
        {/* Image and name */}
        <div className="mt-3 flex h-9 w-full flex-row">
          <div className="h-8 w-8 shrink-0 rounded-full  bg-gray-6 desktop:hidden" />
          <div className="ml-2 h-9 w-[203px] rounded-[4px] bg-gray-6 desktop:ml-0 desktop:w-full" />
        </div>
        {/* price and tick */}
        <div className="mt-4 flex justify-between">
          <div className="h-5 w-1/3 rounded-[4px] bg-gray-6" />
          <div className="h-5 w-1/3 rounded-[4px] bg-gray-6" />
        </div>
      </div>
    );

  const { price, changePercent24H, type, name, code: symbol } = instrument;

  return (
    <Link href={`/assets/${symbol}`}>
      <a className="w-full">
        <button
          className={cn(
            'cursor-pointer w-full overflow-hidden rounded-xl border border-gray-6 p-3 text-left hover:border-black transition-colors',
            className,
          )}
          type="button"
          onClick={() =>
            trackUserEvent(`[Card List] Asset clicked.`, {
              url: `/assets/${symbol}`,
              symbol,
            })
          }
        >
          <div className="grid grid-cols-1">
            <p className="w-full text-black text-body-3">{category}</p>
            <div className="my-4 flex items-center mobile:mt-3 mobile:mb-4">
              <div className="h-8 w-8 shrink-0 rounded-full border border-gray-6 mobile:block">
                <AssetLogo width={40} height={40} alt="Company Logo" type={type} assetName={name} />
              </div>

              <div className="ml-2 flex">
                <div className="flex flex-col">
                  <p className="max-w-40 truncate font-medium text-subtitle-1">{name}</p>
                  <div className="text-gray-4 text-body-3">{symbol}</div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <p className="text-body-1">${pFormatter(price)}</p>
              <div>
                <p className="flex items-center gap-1 whitespace-nowrap text-right text-subtitle-1 mobile:gap-2">
                  <TrendArrow
                    direction={
                      // eslint-disable-next-line no-nested-ternary
                      changePercent24H !== 0
                        ? Math.sign(changePercent24H) === 1
                          ? 'up'
                          : 'down'
                        : 'straight'
                    }
                    width={19}
                    height={14}
                    className="hidden mobile:inline"
                  />
                  <span className="flex-1 font-medium mobile:hidden">
                    {/* eslint-disable-next-line no-nested-ternary */}
                    {changePercent24H !== 0 ? (Math.sign(changePercent24H) === 1 ? '↑' : '↓') : '→'}
                  </span>
                  <span className="text-body-1">{changePercent24H?.toFixed(2)}%</span>
                </p>
              </div>
            </div>
          </div>
        </button>
      </a>
    </Link>
  );
};

export default Card;
